import React, { FC } from 'react'
import styled, { useTheme } from 'styled-components'
import Container from 'ui/Container'
import NavB from 'ui/NavBar'

import useLocale from 'hooks/useLocale'

import { DEFAULT_LOCALE, GATSBY_MAIN_URL, isEmptyHeaderPage } from '@constants'
import { LogoIcon } from 'assets/svg'
import { Link } from 'gatsby'
import useCheckAuth from 'hooks/useCheckAuth'
import TranslateProvider from 'src/providers/TranslateProvider/TranslateProvider'
import { useMediaLayout } from 'use-media'
import useCollaterals from '../../hooks/useCollaterals'
import { useLeadersQuery } from '../../queries'
import AuthLinks from './components/AuthLinks'
import DropdownApp from './components/DropdownApp'
import LocalesDropdown from './components/LocalesDropdown'
import MobileNavbar from './components/MobileNavBar'
import NavBar from './components/NavBar'
import RefferalItem from './components/RefferalItem'
import { AppDownloaderContainer } from './components/AppDownloaderContainer/AppDownloaderContainer'

const Header: FC<NavBarProps> = ({ translate, type = 'primary', pageLocales }) => {
  const { locale, pathname } = useLocale()
  const { device } = useTheme()
  const isTabletResolution = useMediaLayout(device.xxmMax)
  const isMobileResolution = useMediaLayout(device.smMax)
  const withAuth = useCheckAuth()
  const collaterals = useCollaterals()
  const collteralsWithoutAirUSDT = collaterals?.filter(item => item.asset !== 'AIR_USDT')?.map(item => item.id)

  useLeadersQuery({
    options: { staleTime: Infinity, enabled: Boolean(collteralsWithoutAirUSDT?.length) },
    copytradingLeadersParams: {
      max: 10,
      offset: 0,
      filters: { roe: 'ALL', collateralIds: collteralsWithoutAirUSDT },
      sort: 'RATING_ASC',
    },
  })

  const isBlog = isEmptyHeaderPage(pathname)

  const l = locale === 'en' ? '' : `${locale}/`
  const refTo = isBlog ? `/page/${l}referral` : '/referral'

  const iframeOnMouseOverMessage = () => (isBlog ? window.parent.postMessage('true', '*') : null)
  const iframeOnMouseLeaveMessage = () => (isBlog ? window.parent.postMessage('false', '*') : null)

  return (
    <TranslateProvider translate={translate}>
      <HeaderContainer $type={type} onMouseOver={iframeOnMouseOverMessage} onMouseLeave={iframeOnMouseLeaveMessage}>
        <ContainerStyled>
          <Nav>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <NavBrand
                href={
                  locale === DEFAULT_LOCALE ? `${GATSBY_MAIN_URL}/${DEFAULT_LOCALE}` : `${GATSBY_MAIN_URL}/${locale}`
                }
                to={locale === DEFAULT_LOCALE ? `${GATSBY_MAIN_URL}/${DEFAULT_LOCALE}` : `${GATSBY_MAIN_URL}/${locale}`}
                as={isEmptyHeaderPage(pathname) ? 'a' : Link}>
                <StyledLogoIcon $type={type} width={137} height={28} />
              </NavBrand>
              {!isTabletResolution && (
                <DropdownAppContainer>
                  <DropdownApp type={type} />
                </DropdownAppContainer>
              )}
            </div>
            {!isTabletResolution && <NavBar type={type} />}
            {!isTabletResolution && (
              <RefferalItemWrapper>
                <RefferalItem withLocale href={refTo} type={type} />
              </RefferalItemWrapper>
            )}
            <ButtonsContainer>
              <Wrapper>
                <div style={{ display: 'flex', alignItems: 'center', marginRight: '12px' }}>
                  <LocalesDropdown type={type} pageLocales={pageLocales} withAuth={withAuth} />
                  <AppDownloaderContainer />
                </div>
                {!isMobileResolution && (
                  <DesctopButtons>
                    <AuthLinks type={type} withAuth={withAuth} />
                  </DesctopButtons>
                )}
              </Wrapper>

              {isTabletResolution && <MobileNavbar pageLocales={pageLocales} withAuth={withAuth} type={type} />}
            </ButtonsContainer>
          </Nav>
        </ContainerStyled>
      </HeaderContainer>
    </TranslateProvider>
  )
}

export default Header

interface NavBarProps {
  type?: THeaderType
  pageLocales?: string[]
  translate: { [key: string]: string } | null | undefined
}

export type THeaderType = 'primary' | 'secondary'

const HeaderContainer = styled.header<{ $type: THeaderType }>`
  position: relative;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
  background-color: ${({
    $type,
    theme: {
      colors: { newDarkColor, newLightColor },
    },
  }) => ($type === 'secondary' ? newDarkColor?.darkBlue : newLightColor?.white)};
`

const ContainerStyled = styled(Container)`
  position: relative;
  @media ${({ theme }) => theme.device.xl} {
    max-width: 1440px;
    padding-right: 40px;
    padding-inline-start: 40px;
  }
`

const Nav = styled(NavB)`
  flex-wrap: nowrap;
  padding: 12px 0;
  /* @media ${({ theme }) => theme.device.lg} {
    justify-content: space-between;
  } */
`

const NavBrand = styled(Link)`
  display: flex;
  align-items: center;
  max-width: 134px;
  order: -1;
  padding-top: 0.3125rem;
  padding-bottom: 0.3125rem;
  margin-inline-end: 12px;
  font-size: 1.25rem;
  line-height: inherit;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.link};

  :hover {
    color: ${({ theme }) => theme.colors.link};
  }
`
const DropdownAppContainer = styled.div`
  @media ${({ theme }) => theme.device.lg} {
    margin-inline-end: 12px;
  }
`

const StyledLogoIcon = styled(LogoIcon)<{ $type: THeaderType }>`
  .logo-label {
    fill: ${({ $type, theme: { colors } }) => ($type === 'secondary' ? colors.newDarkColor.white : colors.default)};
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  /* order: -1; */
  @media ${({ theme }) => theme.device.lg} {
    margin-inline-start: auto;
    /* order: 0; */
  }
`
const Wrapper = styled.div`
  /* min-width: 255px; */
  display: flex;
  /* justify-content: flex-end; */
`
const DesctopButtons = styled.div`
  /* display: none; */
  display: flex;
  gap: 6px;
  align-items: center;
  @media ${({ theme }) => theme.device.lgMax} {
    & > a {
      padding: 0.625rem 1.375rem;
      white-space: nowrap;
      height: 38px;
    }
  }
`

const RefferalItemWrapper = styled.div`
  display: block;
  margin-inline-end: 12px;
`
